import { ErrorResponse } from "@boi/backend-utils";
import { BulkCreateQuestionBankReqModel, ClassRequestModel, CommonResponse, CreateQuestionBankReqModel, LatestNewQuestionModel, LatestNewQuestionsResponse, LatestQuestionBankViewReq, QNPIdReq, QuestionBankResponse, QuestionBankViewReq, QuestionsCountModel, SubjectClassReqModel, SubjectPivotCount } from "@boi/shared-models";
import { Injectable } from "@nestjs/common";
import { InjectRepository } from "@nestjs/typeorm";
import { ILike, In, Repository } from "typeorm";
import { ChapterRepo } from "../ai-generated-qp/repo/chapter-repo";
import { DepartmentRepo } from "../ai-generated-qp/repo/department-repo";
import { SubjectRepo } from "../ai-generated-qp/repo/subject-repo";
import { TopicRepo } from "../ai-generated-qp/repo/topic-repo";
import { CreateQuestionAdapter } from "./dtos/create-question.adapter";
import { CollegeCodesAndSymbolsRepo } from "./repos/college-codes-symbols.repo";
import { CreateQuestionRepo } from "./repos/create-question-repo";
import { LanguagesRepo } from "../ai-generated-qp/repo/languages-repo";
import { CreateQuestion } from "./entities/create-question";
import { QuestionOption } from "./entities/question-option";
import { AuthenticationEntity } from "../authentications/entities/authentications.entity";
import { AbilityRepo } from "../ai-generated-qp/repo/ability-repo";
import { AbilityTypeRepo } from "../ai-generated-qp/repo/ability-type-repo";
import { ComplexityRepo } from "../ai-generated-qp/repo/complexity-repo";
import { QuestionTypeMasterRepo } from "../ai-generated-qp/repo/question-type-master-repo";
import { CompetencyCodeRepo } from "../ai-generated-qp/repo/competency-code-repo";
import { QnoCountReqModelDto } from "./dtos/qnos-count.dto";
import { buildQuestionCode, getFinancialYear } from "./utils/question-reference.util";


@Injectable()
export class QuestionBankService {
  constructor(
    private qBankRepo: CreateQuestionRepo,
    private qBankAdapter: CreateQuestionAdapter,
    private CollegeCodesRepo: CollegeCodesAndSymbolsRepo,
    private departmentRepo: DepartmentRepo,
    private subjectMasterRepo: SubjectRepo,
    private chapterMasterRepo: ChapterRepo,
    private topicMasterRepo: TopicRepo,
    private languageMasterRepo: LanguagesRepo,
    private abilityRepo: AbilityRepo,
    private abilityTypeRepo: AbilityTypeRepo,
    private complexityRepo: ComplexityRepo,
    private qTypeMasterRepo: QuestionTypeMasterRepo,
    private competencyCodeRepo: CompetencyCodeRepo,
    @InjectRepository(AuthenticationEntity)
    private userRepo: Repository<AuthenticationEntity>,
    @InjectRepository(QuestionOption)
    private questionOptionRepo: Repository<QuestionOption>
  ) { }

  private async getNextRefNo(refYear: string): Promise<number> {
    const row: any = await this.qBankRepo.createQueryBuilder('q')
      .select('MAX(q.refNo)', 'maxRefNo')
      .where('q.refYear = :refYear', { refYear })
      .getRawOne();

    return (row && row.maxRefNo != null) ? Number(row.maxRefNo) + 1 : 1;
  }

  async createQuestionBank(req: any[]): Promise<CommonResponse> {
    if (!Array.isArray(req) || req.length === 0) {
      return new CommonResponse(false, 400, 'No questions provided.');
    }

    const validReqs = req.filter(item => {
      const hasContent = item.question &&
        item.question.trim() !== '' &&
        item.question !== '<p><br></p>' &&
        item.question !== '<p></p>';
      return hasContent;
    });

    if (validReqs.length === 0) {
      return new CommonResponse(false, 400, 'No valid question content provided.');
    }

    console.log("Valid requests to create question", validReqs);

    const savedEntities = [];

    const firstReq = req[0];
    const createdBy = firstReq.userName || null;

    const authUser = createdBy ? await this.userRepo.findOne({ where: { userName: createdBy } }) : null;
    const userMobileNo = authUser?.mobileNo || null;

    const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: firstReq.subject } });

    const refYear = getFinancialYear();
    const refNo = await this.getNextRefNo(refYear);
    const questionCode = buildQuestionCode(sourceSubject?.subjectCode, sourceSubject?.subject, refYear, refNo);

    for (const item of validReqs) {
      let localizedSubjectId = item.subject;

      if (sourceSubject && sourceSubject.subjectParentId > 0) {
        const localizedSubject = await this.subjectMasterRepo.findOne({
          where: {
            subjectParentId: sourceSubject.subjectParentId,
            language: item.language,
            class: item.classId
          }
        });
        if (localizedSubject) {
          localizedSubjectId = localizedSubject.id;
        }
      }

      const dto: CreateQuestionBankReqModel = {
        ...item,
        subject: localizedSubjectId,
        qno: item.qno || questionCode,
        question: item.question || '',
        mobileNo: userMobileNo
      };

      const entity = this.qBankAdapter.convertDtoToEntity(dto, createdBy);
      entity.refNo = refNo;
      entity.refYear = refYear;
      const savedQuestion = await this.qBankRepo.save(entity);
      savedEntities.push(savedQuestion);

      const options = Array.isArray(item.options) ? item.options : [];
      const optionEntities = options
        .filter((opt: any) => opt && opt.optionNo && opt.optionText !== undefined && opt.optionText !== null && opt.optionText !== '')
        .map((opt: any) => this.questionOptionRepo.create({
          questionId: savedQuestion.id,
          optionNo: Number(opt.optionNo),
          optionText: opt.optionText
        }));
      if (optionEntities.length > 0) {
        await this.questionOptionRepo.save(optionEntities);
      }
    }

    return new CommonResponse(true, 2553, 'Questions Created Successfully', savedEntities);
  }

  async updateQuestionBank(req: any): Promise<CommonResponse> {
    const savedEntities = [];
    const localizedUpdates = req.localizedQuestions || [];

    const updatedBy = req.userName || null;

    const sourceSubject = req.subject ? await this.subjectMasterRepo.findOne({ where: { id: req.subject } }) : null;
    let familySubjects: any[] = [];
    if (sourceSubject) {
      const targetParentId = Number(sourceSubject.subjectParentId) > 0 ? Number(sourceSubject.subjectParentId) : sourceSubject.id;
      familySubjects = await this.subjectMasterRepo.find({
        where: [
          { subjectParentId: targetParentId },
          { id: targetParentId }
        ]
      });
    }
    const resolveSubjectForLang = (langId: number, fallback: number) => {
      const match = familySubjects.find(s => Number(s.language) === Number(langId));
      return match ? match.id : fallback;
    };

    let sharedRefNo: number = null;
    let sharedRefYear: string = null;
    const existingRowIds = localizedUpdates.map((l: any) => l.id).filter(Boolean);
    if (existingRowIds.length > 0) {
      const existingRow = await this.qBankRepo.findOne({ where: { id: In(existingRowIds) } });
      sharedRefNo = existingRow?.refNo ?? null;
      sharedRefYear = existingRow?.refYear ?? null;
    }
    if (sharedRefNo == null) {
      sharedRefYear = getFinancialYear();
      sharedRefNo = await this.getNextRefNo(sharedRefYear);
    }

    const saveOptionsForQuestion = async (questionId: number, options: any[]) => {
      await this.questionOptionRepo.delete({ questionId });
      const rows = (Array.isArray(options) ? options : [])
        .filter((opt: any) => opt && opt.optionNo && opt.optionText !== undefined && opt.optionText !== null && opt.optionText !== '')
        .map((opt: any) => this.questionOptionRepo.create({
          questionId,
          optionNo: Number(opt.optionNo),
          optionText: opt.optionText
        }));
      if (rows.length > 0) await this.questionOptionRepo.save(rows);
    };

    for (const langUpdate of localizedUpdates) {
      if (!langUpdate.question && langUpdate.question !== '') continue;

      const commonFields = {
        marks: req.marks,
        complexity: req.complexity || 0,
        questiontype: req.questionTypeMaster || req.questiontype || 0,
        updatedBy,
        preparedBy: req.preparedBy,
        mobileNo: req.mobileNo,
        source: req.source ?? null,
        previousAppearanceCount: Number(req.previousAppearanceCount) || 0,
        answerKey: langUpdate.answerKey || req.answerKey || null,
        approvalStatus: 'PENDING',
        approvedBy: null,
        approvedDate: null,
      };

      const encodedQuestion = langUpdate.question;

      if (langUpdate.id) {
        await this.qBankRepo.update({ id: langUpdate.id }, {
          ...commonFields,
          question: encodedQuestion,
          refNo: sharedRefNo,
          refYear: sharedRefYear
        });
        await saveOptionsForQuestion(langUpdate.id, langUpdate.options);
      } else if (langUpdate.langId && langUpdate.question) {
        const newQuestion = new CreateQuestion();
        Object.assign(newQuestion, {
          ...commonFields,
          createdBy: updatedBy,
          department: req.classId,
          subject: resolveSubjectForLang(langUpdate.langId, langUpdate.subject),
          language: langUpdate.langId,
          qno: req.qno,
          refNo: sharedRefNo,
          refYear: sharedRefYear,
          question: encodedQuestion,
          isActive: true
        });
        const saved = await this.qBankRepo.save(newQuestion);
        savedEntities.push(saved);
        await saveOptionsForQuestion(saved.id, langUpdate.options);
      }
    }

    return new CommonResponse(true, 2554, "Question Updated Successfully", savedEntities);
  }

  async getRandomQuestionsForModeration(req: any): Promise<CommonResponse> {
    try {
      const noOfQuestions = Number(req.noOfQuestions);
      const noOfSets = Number(req.noOfSets) || 1;

      if (!noOfQuestions || noOfQuestions < 1) {
        return new CommonResponse(false, 400, 'Number of questions must be at least 1');
      }
      if (noOfSets < 1) {
        return new CommonResponse(false, 400, 'Number of sets must be at least 1');
      }

      const totalRequired = noOfQuestions * noOfSets;

      const pickedQnos = (await this.qBankRepo.getRandomQnos(totalRequired))
        .map((row: any) => row.qno)
        .filter(Boolean);

      if (pickedQnos.length === 0) {
        return new CommonResponse(false, 404, 'No active and approved questions found');
      }

      if (pickedQnos.length < totalRequired) {
        return new CommonResponse(
          false,
          400,
          `Only ${pickedQnos.length} approved question(s) are available. ${noOfSets} set(s) of ${noOfQuestions} question(s) need ${totalRequired} questions.`
        );
      }

      const rows = await this.qBankRepo.getQuestionRowsByQnos(pickedQnos);

      const optionsByQuestionId = new Map<number, any[]>();
      const optionRows = await this.questionOptionRepo.find({
        where: { questionId: In(rows.map((r: any) => r.id)) },
        order: { optionNo: 'ASC' }
      });
      for (const opt of optionRows) {
        if (!optionsByQuestionId.has(opt.questionId)) optionsByQuestionId.set(opt.questionId, []);
        optionsByQuestionId.get(opt.questionId).push({ optionNo: opt.optionNo, optionText: opt.optionText });
      }

      const languageOrder = (name: string) => {
        const upper = String(name || '').toUpperCase();
        if (upper === 'ENGLISH') return 0;
        if (upper === 'TELUGU') return 1;
        return 2;
      };

      const grouped = new Map<string, any>();
      for (const row of rows) {
        if (!grouped.has(row.qno)) {
          grouped.set(row.qno, {
            qno: row.qno,
            refNo: row.refNo,
            marks: row.marks,
            subjectName: row.subjectName,
            approvalStatus: row.approvalStatus,
            answerKey: row.answerKey,
            translations: []
          });
        }
        const question = grouped.get(row.qno);
        if (!question.answerKey && row.answerKey) question.answerKey = row.answerKey;
        question.translations.push({
          id: row.id,
          languageId: row.languageId,
          languageName: row.languageName || 'ENGLISH',
          question: row.question,
          options: optionsByQuestionId.get(row.id) || []
        });
      }

      const orderedQuestions = pickedQnos
        .map((qno: string) => grouped.get(qno))
        .filter(Boolean)
        .map((question: any) => ({
          ...question,
          translations: question.translations.sort((a: any, b: any) => languageOrder(a.languageName) - languageOrder(b.languageName))
        }));

      const sets = [];
      for (let setIndex = 0; setIndex < noOfSets; setIndex++) {
        sets.push({
          setNo: setIndex + 1,
          questions: orderedQuestions.slice(setIndex * noOfQuestions, (setIndex + 1) * noOfQuestions)
        });
      }

      return new CommonResponse(true, 200, `${sets.length} set(s) of ${noOfQuestions} question(s) generated`, sets);
    } catch (error) {
      console.error('[ERROR] getRandomQuestionsForModeration:', error);
      return new CommonResponse(false, 500, 'Error picking questions', error.message);
    }
  }

  async getAllQuestionBankData(req: QuestionBankViewReq): Promise<QuestionBankResponse> {
    const savedData = await this.qBankRepo.getAllQuestionBankData(req);
    return new QuestionBankResponse(true, 123, 'All Questions retrieved Successfully', savedData);
  }

  async GetSingleQuestionBankData(req: QuestionBankViewReq): Promise<LatestNewQuestionsResponse> {
    try {
      const { rawData, availableLanguages } = await this.qBankRepo.GetSingleQuestionBankData(req);

      const questionIds = [...new Set(rawData.map((r: any) => r.question_id).filter(Boolean))] as number[];
      const optionsByQuestionId = new Map<number, any[]>();
      if (questionIds.length > 0) {
        const optionRows = await this.questionOptionRepo.find({
          where: { questionId: In(questionIds) },
          order: { optionNo: 'ASC' }
        });
        for (const opt of optionRows) {
          if (!optionsByQuestionId.has(opt.questionId)) optionsByQuestionId.set(opt.questionId, []);
          optionsByQuestionId.get(opt.questionId).push({ optionNo: opt.optionNo, optionText: opt.optionText });
        }
      }

      const groupedData = new Map<string, LatestNewQuestionModel>();

      for (const item of rawData) {
        const groupKey = `${item.class_id}_${item.subject_id}_${item.qno}`;
        if (!groupedData.has(groupKey)) {
          groupedData.set(groupKey, new LatestNewQuestionModel({
            id: item.question_id,
            qno: item.qno,
            refNo: item.refNo,
            refYear: item.refYear,
            marks: item.marks,
            className: item.className,
            isActive: item.isActive === 1,
            difficulty: item.difficulty,
            complexity: item.complexity,
            ability: item.ability,
            abilityType: item.abilityType,
            questionType: item.questionType,
            hasStimulus: item.hasStimulus,
            estimatedTime: item.estimatedTime,
            competencyCode: item.competencyCode,
            class_id: item.class_id,
            classId: item.class_id,
            translations: {},
            complexity_id: Number(item.complexity || 0),
            questionType_id: Number(item.questionType || item.questiontype || 0),
            preparedBy: item.preparedBy,
            mobileNo: item.mobileNo,
            hasAnswerKey: item.hasAnswerKey,
            answerKey: item.answerKey,
            preparedDate: item.preparedDate,
            previousAppearanceCount: item.previousAppearanceCount,
            approvalStatus: item.approvalStatus,
            approvedBy: item.approvedBy,
            approvedDate: item.approvedDate
          } as any));
        }

        const model = groupedData.get(groupKey);
        const lang = item.langName || 'ENGLISH';

        model.translations[lang] = {
          id: item.question_id,
          question: item.question,
          subject: item.subjectName,
          chapter: item.chapterName,
          subject_id: item.subject_id,
          chapter_id: item.chapter_id,
          complexity_id: Number(item.complexity || 0),
          questionType_id: Number(item.questionType || item.questiontype || 0),
          hasAnswerKey: item.hasAnswerKey,
          answerKey: item.answerKey,
          source: item.source,
          options: optionsByQuestionId.get(item.question_id) || []
        } as any;

        (model as any).source = (model as any).source || item.source;

        if (lang === 'ENGLISH' || !model.question) {
          model.question = item.question;
          model.subject = item.subjectName;
          model.chapter = item.chapterName;
          model.subject_id = item.subject_id;
          model.subjectId = item.subject_id;
          model.chapter_id = item.chapter_id;
          model.chapterId = item.chapter_id;
          model.hasAnswerKey = item.hasAnswerKey;
          model.answerKey = item.answerKey;
          (model as any).source = item.source;
          (model as any).options = optionsByQuestionId.get(item.question_id) || [];
        }
      }

      const transformedData = Array.from(groupedData.values());
      return new LatestNewQuestionsResponse(true, 200, 'Questions retrieved successfully', transformedData, availableLanguages);
    } catch (error) {
      console.error('Error in service GetSingleQuestionBankData:', error);
      return new LatestNewQuestionsResponse(false, 500, 'Error retrieving questions', [], []);
    }
  }

  async getAllLatestQuestionBankData(req: LatestQuestionBankViewReq): Promise<any> {
    const newReq = new QuestionBankViewReq(Number(req.classId), Number(req.subject), Number(req.chapter), Number(req.marks), Number(req.language));
    newReq.activeOnly = req.activeOnly;
    return await this.GetSingleQuestionBankData(newReq);
  }

  async activateOrDeactivateQuestion(req: QNPIdReq): Promise<CommonResponse> {
    const find = await this.qBankRepo.findOne({ where: { id: req.qnpId } });
    if (find) {
      await this.qBankRepo.update({ id: req.qnpId }, { isActive: !find.isActive });
      return new CommonResponse(true, 125478, `${find.isActive ? "Deactivated Successfully" : "Activated Successfully"}`);
    } else {
      throw new ErrorResponse(123458, "Status Not Found");
    }
  }

  async approveQuestion(req: any): Promise<CommonResponse> {
    try {
      const qno = String(req.qno);
      const classId = Number(req.classId);
      const subjectId = Number(req.subjectId ?? req.subject);
      const approvedBy = req.approvedBy || req.userName || null;
      const requestedStatus = String(req.approvalStatus || '').toUpperCase();
      const approvalStatus = requestedStatus === 'FINAL' ? 'FINAL'
        : requestedStatus === 'HOLD' ? 'HOLD'
          : requestedStatus === 'PENDING' ? 'PENDING'
            : 'APPROVED';
      const isActionedStatus = approvalStatus !== 'PENDING';

      let subjectIds = [subjectId];
      const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: subjectId } });
      if (sourceSubject) {
        const targetParentId = Number(sourceSubject.subjectParentId) > 0 ? Number(sourceSubject.subjectParentId) : sourceSubject.id;
        const relatedSubjects = await this.subjectMasterRepo.find({
          where: [
            { subjectParentId: targetParentId },
            { id: targetParentId }
          ]
        });
        subjectIds = relatedSubjects.map(s => s.id);
        if (!subjectIds.includes(subjectId)) subjectIds.push(subjectId);
      }

      await this.qBankRepo.createQueryBuilder()
        .update(CreateQuestion)
        .set({
          approvalStatus: approvalStatus,
          approvedBy: isActionedStatus ? approvedBy : null,
          approvedDate: isActionedStatus ? new Date() : null
        })
        .where('qno = :qno AND department = :classId AND subject IN (:...subjectIds)', { qno, classId, subjectIds })
        .execute();

      return new CommonResponse(true, 200, 'Question status updated successfully');
    } catch (error) {
      console.error('[ERROR] approveQuestion:', error);
      return new CommonResponse(false, 500, 'Error approving question', error.message);
    }
  }

  async getQuestionsCountSummery(req: ClassRequestModel): Promise<CommonResponse> {
    const data: QuestionsCountModel = await this.qBankRepo.getQuestionsCountSummery(req);
    return new CommonResponse(true, 125478, 'summery count retrieved successfully', data);
  }

  async getQuestionSubjectPivot(req: ClassRequestModel): Promise<CommonResponse> {
    const data: SubjectPivotCount[] = await this.qBankRepo.getQuestionSubjectPivot(req);
    return new CommonResponse(true, 125478, 'summery count retrieved successfully', data);
  }

  async getAllCollegeCodesAndSymbols(): Promise<CommonResponse> {
    const savedData = await this.CollegeCodesRepo.find();
    return new CommonResponse(true, 2244, 'College Codes And Symbols Retrived Successfully', savedData)
  }

  async getAllDepartments(): Promise<CommonResponse> {
    const departments = await this.departmentRepo.find({ order: { departmentName: 'ASC' } });
    const data = departments.map(d => ({ id: d.id, class: d.departmentName }));
    return new CommonResponse(true, 200, 'Departments retrieved successfully', data);
  }

  async getAllQuestionsCountsBySubject(req: SubjectClassReqModel): Promise<CommonResponse> {
    const savedData = await this.qBankRepo.getAllQuestionsCountsBySubject(req);
    return new CommonResponse(true, 125, 'All Questions retrieved Successfully', savedData);
  }

  private normalizeString(str: string): string {
    return str?.trim()?.toLowerCase() || '';
  }



  // async bulkCreateQuestionBank(req: BulkCreateQuestionBankReqModel): Promise<CommonResponse> {
  //   const results = [];
  //   const errors = [];

  //   console.log("req----------", req)
  //   const departmentCache = new Map();
  //   const subjectCache = new Map();
  //   const complexityCache = new Map();

  //   const refYear = getFinancialYear();
  //   let nextRefNo = await this.getNextRefNo(refYear);

  //   for (let i = 0; i < req.questions.length; i++) {
  //     const item = req.questions[i];
  //     try {
  //       // 1. Resolve Department
  //       const departmentName = item.department?.trim();
  //       if (!departmentName) {
  //         throw new Error(`Department is missing at row ${i + 1}`);
  //       }

  //       const normalizedDepartment = this.normalizeString(departmentName);
  //       let departmentId = departmentCache.get(normalizedDepartment);
  //       if (!departmentId) {
  //         const deptEnt = await this.departmentRepo.findOne({
  //           where: { departmentName: ILike(departmentName) }
  //         });
  //         if (!deptEnt) {
  //           throw new Error(`Department '${departmentName}' not found in the system at row ${i + 1}`);
  //         }
  //         departmentId = deptEnt.id;
  //         departmentCache.set(normalizedDepartment, departmentId);
  //       }

  //       // 2. Resolve Subject
  //       const subjectName = item.subjectName?.trim();
  //       if (!subjectName) {
  //         throw new Error(`Subject is missing at row ${i + 1}`);
  //       }

  //       const normalizedSubject = this.normalizeString(subjectName);
  //       let subjectEntity = subjectCache.get(normalizedSubject);
  //       if (!subjectEntity) {
  //         const subEnt = await this.subjectMasterRepo.findOne({
  //           where: {
  //             subject: ILike(subjectName),
  //             language: 2
  //           }
  //         });
  //         if (!subEnt) {
  //           const subEntFallback = await this.subjectMasterRepo.findOne({
  //             where: {
  //               subject: ILike(subjectName)
  //             }
  //           });
  //           if (!subEntFallback) {
  //             throw new Error(`Subject '${subjectName}' not found in the system at row ${i + 1}`);
  //           }
  //           subjectEntity = subEntFallback;
  //         } else {
  //           subjectEntity = subEnt;
  //         }
  //         subjectCache.set(normalizedSubject, subjectEntity);
  //       }
  //       const subjectId = subjectEntity.id;

  //       // 3. Resolve Complexity (Difficulty from Excel)
  //       const difficultyName = item.difficultyName?.trim();
  //       if (!difficultyName) {
  //         throw new Error(`Difficulty is missing at row ${i + 1}`);
  //       }

  //       const normalizedDifficulty = this.normalizeString(difficultyName);
  //       let complexityId = complexityCache.get(normalizedDifficulty);
  //       if (!complexityId) {
  //         const comEnt = await this.complexityRepo.findOne({
  //           where: { complexity: ILike(difficultyName) }
  //         });
  //         if (!comEnt) {
  //           throw new Error(`Difficulty '${difficultyName}' not found in the system at row ${i + 1}`);
  //         }
  //         complexityId = comEnt.id;
  //         complexityCache.set(normalizedDifficulty, complexityId);
  //       }

  //       const entity = new CreateQuestion();
  //       entity.department = departmentId;
  //       entity.subject = subjectId;
  //       entity.qno = buildQuestionCode(subjectEntity.subjectCode, subjectEntity.subject, refYear, nextRefNo);
  //       entity.refNo = nextRefNo;
  //       entity.refYear = refYear;
  //       entity.question = item.questionEn || '';
  //       entity.language = 2;
  //       entity.marks = 1;
  //       entity.complexity = complexityId;
  //       entity.questiontype = 1;
  //       entity.isActive = true;
  //       entity.createdBy = item.createdBy || null;
  //       entity.updatedBy = null;
  //       entity.preparedBy = item.preparedBy || '';
  //       entity.mobileNo = item.mobileNo || '';
  //       entity.answerKey = item.answerKey || null;
  //       entity.source = item.source || '';
  //       entity.approvedBy = '';
  //       entity.approvedDate = new Date();

  //       console.log("entitytttttt", entity)
  //       // Save the question entity
  //       const savedEntity = await this.qBankRepo.save(entity);

  //       if (savedEntity) {
  //         nextRefNo++;

  //         // 6. Save options separately in question_options table
  //         const options = item.options || [];
  //         console.log("Options received for row", i, ":", options);
  //         if (options && options.length > 0) {
  //           const optionEntities = options
  //             .filter(opt => opt.optionText && opt.optionText.trim() !== '')
  //             .map(opt =>
  //               this.questionOptionRepo.create({
  //                 questionId: savedEntity.id,
  //                 optionNo: opt.optionNo,
  //                 optionText: opt.optionText
  //               })
  //             );
  //           console.log("optionEntities", optionEntities)
  //           if (optionEntities.length > 0) {
  //             await this.questionOptionRepo.save(optionEntities);
  //           }
  //         }

  //         results.push({
  //           index: i,
  //           status: 'success',
  //           id: savedEntity.id,
  //           qno: savedEntity.qno,
  //           refNo: savedEntity.refNo,
  //           refYear: savedEntity.refYear,
  //           optionsCount: options.length
  //         });
  //       } else {
  //         errors.push({
  //           index: i,
  //           error: 'Failed to save question'
  //         });
  //       }

  //     } catch (err) {
  //       errors.push({
  //         index: i,
  //         error: err.message || 'Unknown error occurred'
  //       });
  //     }
  //   }

  //   return new CommonResponse(
  //     errors.length === 0,
  //     errors.length === 0 ? 200 : 207,
  //     errors.length === 0
  //       ? `Successfully uploaded ${results.length} questions`
  //       : `Uploaded ${results.length} questions with ${errors.length} errors`,
  //     {
  //       total: req.questions.length,
  //       success: results.length,
  //       errors: errors.length,
  //       results: results,
  //       errorDetails: errors
  //     }
  //   );
  // }


  async bulkCreateQuestionBank(req: BulkCreateQuestionBankReqModel): Promise<CommonResponse> {
    const results = [];
    const errors = [];

    console.log("req----------", JSON.stringify(req, null, 2));

    const departmentCache = new Map();
    const subjectCache = new Map();
    const complexityCache = new Map();

    const refYear = getFinancialYear();
    let nextRefNo = await this.getNextRefNo(refYear);

    for (let i = 0; i < req.questions.length; i++) {
      const item = req.questions[i];
      try {
        // 1. Resolve Department
        const departmentName = item.department?.trim();
        if (!departmentName) {
          throw new Error(`Department is missing at row ${i + 1}`);
        }

        const normalizedDepartment = this.normalizeString(departmentName);
        let departmentId = departmentCache.get(normalizedDepartment);
        if (!departmentId) {
          const deptEnt = await this.departmentRepo.findOne({
            where: { departmentName: ILike(departmentName) }
          });
          if (!deptEnt) {
            throw new Error(`Department '${departmentName}' not found in the system at row ${i + 1}`);
          }
          departmentId = deptEnt.id;
          departmentCache.set(normalizedDepartment, departmentId);
        }

        // 2. Resolve Subject
        const subjectName = item.subjectName?.trim();
        if (!subjectName) {
          throw new Error(`Subject is missing at row ${i + 1}`);
        }

        const normalizedSubject = this.normalizeString(subjectName);
        let subjectEntity = subjectCache.get(normalizedSubject);
        if (!subjectEntity) {
          const subEnt = await this.subjectMasterRepo.findOne({
            where: {
              subject: ILike(subjectName),
              language: 2
            }
          });
          if (!subEnt) {
            const subEntFallback = await this.subjectMasterRepo.findOne({
              where: {
                subject: ILike(subjectName)
              }
            });
            if (!subEntFallback) {
              throw new Error(`Subject '${subjectName}' not found in the system at row ${i + 1}`);
            }
            subjectEntity = subEntFallback;
          } else {
            subjectEntity = subEnt;
          }
          subjectCache.set(normalizedSubject, subjectEntity);
        }
        const subjectId = subjectEntity.id;

        // 3. Resolve Complexity (Difficulty from Excel)
        const difficultyName = item.difficultyName?.trim();
        if (!difficultyName) {
          throw new Error(`Difficulty is missing at row ${i + 1}`);
        }

        const normalizedDifficulty = this.normalizeString(difficultyName);
        let complexityId = complexityCache.get(normalizedDifficulty);
        if (!complexityId) {
          const comEnt = await this.complexityRepo.findOne({
            where: { complexity: ILike(difficultyName) }
          });
          if (!comEnt) {
            throw new Error(`Difficulty '${difficultyName}' not found in the system at row ${i + 1}`);
          }
          complexityId = comEnt.id;
          complexityCache.set(normalizedDifficulty, complexityId);
        }

        // 4. Prepare versions - English and Telugu
        const versions = [
          {
            questionData: item,
            language: 2,
            languageName: 'English',
            questionText: item.questionEn,
            options: item.options || []
          },
          {
            questionData: item.teluguVersion,
            language: 1,
            languageName: 'Telugu',
            questionText: item.teluguVersion?.questionEn || null,
            options: item.teluguVersion?.options || []
          }
        ].filter(v => v.questionText && v.questionText.trim() !== '');

        if (versions.length === 0) {
          throw new Error(`No question content found in either English or Telugu at row ${i + 1}`);
        }

        let savedEnglishId = null;
        let savedTeluguId = null;
        const savedVersions = [];

        for (const version of versions) {
          const qData = version.questionData;

          // Get the next qno for this department and subject
          const lastQ = await this.qBankRepo.createQueryBuilder('q')
            .where('q.department = :departmentId', { departmentId })
            .andWhere('q.subject = :subjectId', { subjectId })
            .orderBy('q.qno', 'DESC')
            .getOne();

          const newQno = lastQ ? lastQ.qno + 1 : 1;

          // Create the question entity
          const entity = new CreateQuestion();
          entity.department = departmentId;
          entity.subject = subjectId;
          entity.qno = buildQuestionCode(subjectEntity.subjectCode, subjectEntity.subject, refYear, nextRefNo);
          entity.refNo = nextRefNo;
          entity.refYear = refYear;
          entity.question = version.questionText || '';
          entity.language = version.language;
          entity.marks = 1;
          entity.complexity = complexityId;
          entity.questiontype = 1;
          entity.isActive = true;
          entity.createdBy = qData.createdBy || null;
          entity.updatedBy = null;
          entity.preparedBy = qData.preparedBy || '';
          entity.mobileNo = qData.mobileNo || '';
          entity.answerKey = qData.answerKey || null;
          entity.source = qData.source || '';
          entity.approvedBy = '';
          entity.approvedDate = new Date();

          console.log(`Saving ${version.languageName} entity:`, entity);

          // Save the question entity
          const savedEntity = await this.qBankRepo.save(entity);

          if (savedEntity) {
            // Save options for this version
            const options = version.options || [];
            console.log(`Options received for ${version.languageName} row ${i}:`, options);

            if (options && options.length > 0) {
              const optionEntities = options
                .filter(opt => opt.optionText && opt.optionText.trim() !== '')
                .map(opt =>
                  this.questionOptionRepo.create({
                    questionId: savedEntity.id,
                    optionNo: opt.optionNo,
                    optionText: opt.optionText
                  })
                );
              console.log(`Saving ${optionEntities.length} options for ${version.languageName}:`, optionEntities);

              if (optionEntities.length > 0) {
                await this.questionOptionRepo.save(optionEntities);
                console.log(`✅ Saved ${optionEntities.length} options for ${version.languageName} question ${savedEntity.id}`);
              }
            }

            savedVersions.push({
              id: savedEntity.id,
              language: version.language,
              languageName: version.languageName,
              qno: savedEntity.qno
            });

            if (version.language === 2) {
              savedEnglishId = savedEntity.id;
            } else if (version.language === 1) {
              savedTeluguId = savedEntity.id;
            }
          } else {
            throw new Error(`Failed to save ${version.languageName} question`);
          }
        }

        // Increment refNo after successfully saving both versions (or at least one)
        if (savedVersions.length > 0) {
          results.push({
            index: i,
            status: 'success',
            englishId: savedEnglishId,
            teluguId: savedTeluguId || null,
            versions: savedVersions,
            refNo: nextRefNo,
            refYear: refYear
          });
          nextRefNo++;
        } else {
          errors.push({
            index: i,
            error: 'Failed to save question'
          });
        }

      } catch (err) {
        console.error(`❌ Error on row ${i}:`, err);
        errors.push({
          index: i,
          error: err.message || 'Unknown error occurred'
        });
      }
    }
    return new CommonResponse(
      errors.length === 0,
      errors.length === 0 ? 200 : 207,
      errors.length === 0
        ? `Successfully uploaded ${results.length} questions`
        : `Uploaded ${results.length} questions with ${errors.length} errors`,
      {
        total: req.questions.length,
        success: results.length,
        errors: errors.length,
        results: results,
        errorDetails: errors
      }
    );
  }


  // async bulkCreateQuestionBank(req: BulkCreateQuestionBankReqModel): Promise<CommonResponse> {
  //   const results = [];
  //   const errors = [];

  //   console.log("req----------", req)
  //   // Cache for name -> ID mappings within this bulk request
  //   const departmentCache = new Map();
  //   const subjectCache = new Map();
  //   const complexityCache = new Map();

  //   for (let i = 0; i < req.questions.length; i++) {
  //     const item = req.questions[i];
  //     try {
  //       // 1. Resolve Department (from departments table)
  //       const departmentName = item.department?.trim();
  //       if (!departmentName) {
  //         throw new Error(`Department is missing at row ${i + 1}`);
  //       }

  //       const normalizedDepartment = this.normalizeString(departmentName);
  //       let departmentId = departmentCache.get(normalizedDepartment);
  //       if (!departmentId) {
  //         const deptEnt = await this.departmentRepo.findOne({
  //           where: { departmentName: ILike(departmentName) }
  //         });
  //         if (!deptEnt) {
  //           throw new Error(`Department '${departmentName}' not found in the system at row ${i + 1}`);
  //         }
  //         departmentId = deptEnt.id;
  //         departmentCache.set(normalizedDepartment, departmentId);
  //       }

  //       // 2. Resolve Subject
  //       const subjectName = item.subjectName?.trim();
  //       if (!subjectName) {
  //         throw new Error(`Subject is missing at row ${i + 1}`);
  //       }

  //       const normalizedSubject = this.normalizeString(subjectName);
  //       let subjectId = subjectCache.get(normalizedSubject);
  //       if (!subjectId) {
  //         const subEnt = await this.subjectMasterRepo.findOne({
  //           where: {
  //             subject: ILike(subjectName),
  //             language: 2
  //           }
  //         });
  //         if (!subEnt) {
  //           const subEntFallback = await this.subjectMasterRepo.findOne({
  //             where: {
  //               subject: ILike(subjectName)
  //             }
  //           });
  //           if (!subEntFallback) {
  //             throw new Error(`Subject '${subjectName}' not found in the system at row ${i + 1}`);
  //           }
  //           subjectId = subEntFallback.id;
  //         } else {
  //           subjectId = subEnt.id;
  //         }
  //         subjectCache.set(normalizedSubject, subjectId);
  //       }

  //       // 3. Resolve Complexity (Difficulty from Excel)
  //       const difficultyName = item.difficultyName?.trim();
  //       if (!difficultyName) {
  //         throw new Error(`Difficulty is missing at row ${i + 1}`);
  //       }

  //       const normalizedDifficulty = this.normalizeString(difficultyName);
  //       let complexityId = complexityCache.get(normalizedDifficulty);
  //       if (!complexityId) {
  //         const comEnt = await this.complexityRepo.findOne({
  //           where: { complexity: ILike(difficultyName) }
  //         });
  //         if (!comEnt) {
  //           throw new Error(`Difficulty '${difficultyName}' not found in the system at row ${i + 1}`);
  //         }
  //         complexityId = comEnt.id;
  //         complexityCache.set(normalizedDifficulty, complexityId);
  //       }

  //       // 4. Get the next qno for this department and subject
  //       const lastQ = await this.qBankRepo.createQueryBuilder('q')
  //         .where('q.department = :departmentId', { departmentId })
  //         .andWhere('q.subject = :subjectId', { subjectId })
  //         .orderBy('q.qno', 'DESC')
  //         .getOne();

  //       const newQno = lastQ ? lastQ.qno + 1 : 1;

  //       // 5. Create the question entity
  //       const entity = new CreateQuestion();
  //       entity.department = departmentId;
  //       entity.subject = subjectId;
  //       entity.qno = newQno;
  //       entity.question = item.questionEn || '';
  //       entity.language = 2;
  //       entity.marks = 1;
  //       entity.complexity = complexityId;
  //       entity.questiontype = 1;
  //       entity.isActive = true;
  //       entity.createdBy = item.createdBy;
  //       entity.updatedBy = null;
  //       entity.preparedBy = item.preparedBy || '';
  //       entity.mobileNo = item.mobileNo || '';
  //       entity.answerKey = item.answerKey || null;
  //       entity.source = item.source || '';
  //       entity.approvedBy = '';
  //       entity.approvedDate = new Date();

  //       console.log("entitytttttt", entity)
  //       // Save the entity
  //       const savedEntity = await this.qBankRepo.save(entity);

  //       if (savedEntity) {
  //         results.push({
  //           index: i,
  //           status: 'success',
  //           id: savedEntity.id,
  //           qno: savedEntity.qno
  //         });
  //       } else {
  //         errors.push({
  //           index: i,
  //           error: 'Failed to save question'
  //         });
  //       }

  //     } catch (err) {
  //       errors.push({
  //         index: i,
  //         error: err.message || 'Unknown error occurred'
  //       });
  //     }
  //   }

  //   return new CommonResponse(
  //     errors.length === 0,
  //     errors.length === 0 ? 200 : 207,
  //     errors.length === 0
  //       ? `Successfully uploaded ${results.length} questions`
  //       : `Uploaded ${results.length} questions with ${errors.length} errors`,
  //     {
  //       total: req.questions.length,
  //       success: results.length,
  //       errors: errors.length,
  //       results: results,
  //       errorDetails: errors
  //     }
  //   );
  // }

  async getAvailableQuestionsByChapterAndSubAbility(req: QnoCountReqModelDto) {
    const data = await this.qBankRepo.getAvailableQuestionsByChapterAndSubAbility(req)
    return new CommonResponse(true, 125, 'All Questions retrieved Successfully', data);
  }

  async getQuestionAbilityDistribution(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionAbilityDistribution(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question ability distribution retrieved successfully', data);
  }

  async getQuestionCountsReport(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionCountsReport(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question counts report retrieved successfully', data);
  }

  async getQuestionTypeDistribution(req: SubjectClassReqModel): Promise<CommonResponse> {
    const data = await this.qBankRepo.getQuestionTypeDistribution(req.classId, req.subject);
    return new CommonResponse(true, 200, 'Question type distribution retrieved successfully', data);
  }

  async getLanguagesByChapter(chapterId: number): Promise<CommonResponse> {
    const chapter = await this.chapterMasterRepo.findOne({ where: { id: chapterId } });
    if (!chapter) {
      return new CommonResponse(false, 404, 'Chapter not found');
    }

    const targetParentId = chapter.chapterParentId > 0 ? chapter.chapterParentId : chapter.id;
    const allChapters = await this.chapterMasterRepo.find({
      where: [
        { chapterParentId: targetParentId },
        { id: targetParentId }
      ]
    });

    const subjectIds = allChapters.map(c => c.subject);
    if (subjectIds.length === 0) return new CommonResponse(true, 200, 'No related subjects found', []);

    const subjects = await this.subjectMasterRepo.find({
      where: { id: In(subjectIds) }
    });

    const languages = [...new Set(subjects.map(s => s.language))].filter(l => l);

    return new CommonResponse(true, 200, 'Languages retrieved successfully', languages);
  }

  async getSiblingsByRefNo(refNo: number, refYear: string, classId: number, subjectId: number): Promise<CommonResponse> {
    try {
      const sourceSubject = await this.subjectMasterRepo.findOne({ where: { id: subjectId } });
      if (!sourceSubject) {
        return new CommonResponse(false, 404, 'Subject not found');
      }

      const targetParentId = Number(sourceSubject.subjectParentId) > 0 ? Number(sourceSubject.subjectParentId) : sourceSubject.id;
      const relatedSubjects = await this.subjectMasterRepo.find({
        where: [
          { subjectParentId: targetParentId },
          { id: targetParentId }
        ]
      });

      const subjectIds = relatedSubjects.map(s => s.id);

      const siblingWhere: any = {
        refNo: refNo,
        department: classId,
        subject: In(subjectIds),
        isActive: true
      };
      if (refYear) siblingWhere.refYear = refYear;

      const siblings = await this.qBankRepo.find({
        where: siblingWhere,
        order: { id: 'DESC' }
      });

      // Fetch the options for each sibling row, grouped by question id.
      const siblingIds = siblings.map(s => s.id).filter(Boolean);
      const optionsByQid = new Map<number, any[]>();
      if (siblingIds.length > 0) {
        const optRows = await this.questionOptionRepo.find({
          where: { questionId: In(siblingIds) },
          order: { optionNo: 'ASC' }
        });
        for (const o of optRows) {
          if (!optionsByQid.has(o.questionId)) optionsByQid.set(o.questionId, []);
          optionsByQid.get(o.questionId).push({ optionNo: o.optionNo, optionText: o.optionText });
        }
      }

      // Include language names for every language present in the family AND in the
      // actual rows (rows may hold a language not covered by the family subjects).
      const allLangIds = [...new Set([...relatedSubjects.map(s => s.language), ...siblings.map(s => s.language)])];
      const languages = await this.languageMasterRepo.find({
        where: { id: In(allLangIds) }
      });
      const langName = (langId: number) => languages.find(l => l.id === langId)?.language || 'Unknown';

      const finalSiblings = [];
      const usedRowIds = new Set<number>();

      // One slot per family subject, matched by the row's actual language so each
      // language row surfaces correctly even if an older bug linked it to the wrong subject.
      for (const subj of relatedSubjects) {
        const existing = siblings.find(s => Number(s.language) === Number(subj.language) && !usedRowIds.has(s.id))
          || siblings.find(s => s.subject === subj.id && !usedRowIds.has(s.id));

        if (existing) {
          usedRowIds.add(existing.id);
          finalSiblings.push({
            ...existing,
            question: existing.question || '',
            languageName: langName(existing.language),
            options: optionsByQid.get(existing.id) || []
          });
        } else {
          finalSiblings.push({
            id: null,
            qno: siblings[0]?.qno ?? null,
            refNo: refNo,
            refYear: refYear ?? siblings[0]?.refYear ?? null,
            department: classId,
            subject: subj.id,
            language: subj.language,
            languageName: langName(subj.language),
            question: '',
            options: []
          });
        }
      }

      // Emit any remaining fetched rows whose language wasn't covered by a family
      // slot (e.g. a second language stored under the same subject id by an older bug).
      for (const s of siblings) {
        if (usedRowIds.has(s.id)) continue;
        usedRowIds.add(s.id);
        finalSiblings.push({
          ...s,
          question: s.question || '',
          languageName: langName(s.language),
          options: optionsByQid.get(s.id) || []
        });
      }

      return new CommonResponse(true, 200, 'Siblings retrieved successfully', finalSiblings);
    } catch (error) {
      console.error('[ERROR] getSiblingsByRefNo:', error);
      return new CommonResponse(false, 500, 'Error retrieving siblings', error.message);
    }
  }
}
